/* eslint-disable @typescript-eslint/no-empty-function */
import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, ActionCreator, Creator, Store } from '@ngrx/store';
import { switchMap, Observable, map, tap, exhaustMap, filter } from 'rxjs';

export interface effectArgs<T extends ActionCreator<string, Creator>> {
  action: T;
  filterFn?: (value: ReturnType<T>, index: number) => boolean;
  dispatch?: boolean;

  // Map Functions
  switchMapFn: (action: ReturnType<T>) => Observable<Action>;
  exhaustMapFn: (action: ReturnType<T>) => Observable<Action>;
  mapFn: (action: ReturnType<T>) => Action;
  tapFn: (action: ReturnType<T>) => void;
}

// Helper type to infer the output tuple from the factory function
type ConcatLatestFromTuple<F, V> = F extends (value: V) => Observable<infer R> ? [V, R] : never;

export interface effectWithConcatArgs<
  T extends ActionCreator<string, Creator>,
  F extends (action: ReturnType<T>) => Observable<any>,
> {
  action: T;
  filterFn?: (value: ReturnType<T>, index: number) => boolean;
  /**
   * Function to be passed to concatLatestFrom, e.g. (action) => store.select(selector)
   */
  concatLatestFromFn: F;
  dispatch?: boolean;

  // Map Functions
  switchMapFn: (action: ReturnType<T>, latest: ConcatLatestFromTuple<F, ReturnType<T>>[1]) => Observable<Action>;
  exhaustMapFn: (action: ReturnType<T>, latest: ConcatLatestFromTuple<F, ReturnType<T>>[1]) => Observable<Action>;
  mapFn: (action: ReturnType<T>, latest: ConcatLatestFromTuple<F, ReturnType<T>>[1]) => Action;
  tapFn: (action: ReturnType<T>, latest: ConcatLatestFromTuple<F, ReturnType<T>>[1]) => void;
}

export abstract class BaseEffects {
  protected readonly actions$ = inject(Actions);
  protected readonly store = inject(Store);
  protected readonly http = inject(HttpClient);

  /*
    Opinionated way to construct an effect for 90% of use cases.  
    Parameters execute in a specific order - Action -> Filter -> SwitchMap.
  */
  protected switchMapEffect<T extends ActionCreator<string, Creator>>(
    args: Pick<effectArgs<T>, 'action' | 'filterFn' | 'dispatch' | 'switchMapFn'>
  ) {
    const { action, switchMapFn, filterFn, dispatch = true } = args;
    return createEffect(
      () => {
        return this.actions$.pipe(
          ofType(action),
          filterFn ? filter(filterFn) : tap(() => {}),
          switchMap(action => switchMapFn(action))
        );
      },
      !dispatch ? { dispatch: false } : {}
    );
  }

  /*
    Opinionated way to construct an effect for 90% of use cases. 
    Parameters execute in a specific order - Action -> Filter -> ConcatLatestFrom -> SwitchMap.
  */
  protected switchMapWithConcatEffect<
    T extends ActionCreator<string, Creator>,
    F extends (action: ReturnType<T>) => Observable<any>,
  >(args: Pick<effectWithConcatArgs<T, F>, 'action' | 'filterFn' | 'dispatch' | 'concatLatestFromFn' | 'switchMapFn'>) {
    const { action, switchMapFn, filterFn, concatLatestFromFn, dispatch = true } = args;
    return createEffect(
      () => {
        return this.actions$.pipe(
          ofType(action),
          filterFn ? filter(filterFn) : tap(() => {}),
          concatLatestFrom(concatLatestFromFn),
          switchMap(tuple => switchMapFn(tuple[0], tuple[1]))
        );
      },
      !dispatch ? { dispatch: false } : {}
    );
  }

  /*
    Opinionated way to construct an effect for 90% of use cases. 
    Parameters execute in a specific order - Action -> Filter -> Map.
  */
  protected mapEffect<T extends ActionCreator<string, Creator>>(
    args: Pick<effectArgs<T>, 'action' | 'filterFn' | 'dispatch' | 'mapFn'>
  ) {
    const { action, mapFn, filterFn, dispatch = true } = args;
    return createEffect(
      () => this.actions$.pipe(ofType(action), filterFn ? filter(filterFn) : tap(() => {}), map(mapFn)),
      !dispatch ? { dispatch: false } : {}
    );
  }

  /*
    Opinionated way to construct an effect for 90% of use cases. 
    Parameters execute in a specific order - Action -> Filter -> ConcatLatestFrom -> map.
  */
  protected mapWithConcatEffect<
    T extends ActionCreator<string, Creator>,
    F extends (action: ReturnType<T>) => Observable<any>,
  >(args: Pick<effectWithConcatArgs<T, F>, 'action' | 'filterFn' | 'dispatch' | 'concatLatestFromFn' | 'mapFn'>) {
    const { action, mapFn, filterFn, concatLatestFromFn, dispatch = true } = args;
    return createEffect(
      () =>
        this.actions$.pipe(
          ofType(action),
          filterFn ? filter(filterFn) : tap(() => {}),
          concatLatestFrom(concatLatestFromFn),
          map(tuple => mapFn(tuple[0], tuple[1]))
        ),

      !dispatch ? { dispatch: false } : {}
    );
  }

  /*
    Opinionated way to construct an effect for 90% of use cases. 
    Parameters execute in a specific order - Action -> Filter -> Tap.
  */
  protected tapEffect<T extends ActionCreator<string, Creator>>(
    args: Pick<effectArgs<T>, 'action' | 'filterFn' | 'tapFn'>
  ) {
    const { action, tapFn, filterFn } = args;
    return createEffect(
      () => this.actions$.pipe(ofType(action), filterFn ? filter(filterFn) : tap(() => {}), tap(tapFn)),
      { dispatch: false }
    );
  }

  /*
    Opinionated way to construct an effect for 90% of use cases. 
    Parameters execute in a specific order - Action -> Filter -> ConcatLatestFrom -> Tap.
  */
  protected tapWithConcatEffect<
    T extends ActionCreator<string, Creator>,
    F extends (action: ReturnType<T>) => Observable<any>,
  >(args: Pick<effectWithConcatArgs<T, F>, 'action' | 'filterFn' | 'concatLatestFromFn' | 'tapFn'>) {
    const { action, tapFn, filterFn, concatLatestFromFn } = args;
    return createEffect(
      () =>
        this.actions$.pipe(
          ofType(action),
          filterFn ? filter(filterFn) : tap(() => {}),
          concatLatestFrom(concatLatestFromFn),
          tap(tuple => tapFn(tuple[0], tuple[1]))
        ),
      { dispatch: false }
    );
  }

  /*
    Opinionated way to construct an effect for 90% of use cases. 
    Parameters execute in a specific order - Action -> Filter -> ExhaustMap.
  */
  protected exhaustMapEffect<T extends ActionCreator<string, Creator>>(
    args: Pick<effectArgs<T>, 'action' | 'filterFn' | 'dispatch' | 'exhaustMapFn'>
  ) {
    const { action, exhaustMapFn, filterFn, dispatch = true } = args;
    return createEffect(
      () => {
        return this.actions$.pipe(
          ofType(action),
          filterFn ? filter(filterFn) : tap(() => {}),
          exhaustMap(action => exhaustMapFn(action))
        );
      },
      !dispatch ? { dispatch: false } : {}
    );
  }

  /*
    Opinionated way to construct an effect for 90% of use cases. 
    Parameters execute in a specific order - Action -> Filter -> ConcatLatestFrom -> ExhaustMap.
  */
  protected exhaustMapWithConcatEffect<
    T extends ActionCreator<string, Creator>,
    F extends (action: ReturnType<T>) => Observable<any>,
  >(
    args: Pick<effectWithConcatArgs<T, F>, 'action' | 'filterFn' | 'dispatch' | 'concatLatestFromFn' | 'exhaustMapFn'>
  ) {
    const { action, exhaustMapFn, filterFn, concatLatestFromFn, dispatch = true } = args;
    return createEffect(
      () => {
        return this.actions$.pipe(
          ofType(action),
          filterFn ? filter(filterFn) : tap(() => {}),
          concatLatestFrom(concatLatestFromFn),
          exhaustMap(tuple => exhaustMapFn(tuple[0], tuple[1]))
        );
      },
      !dispatch ? { dispatch: false } : {}
    );
  }
}
